import { Box } from '@dtx-company/shared-components/src/components/atoms/Box/index'
import { COMBINED_FUNNEL_FORM_INPUT_ENTRIES } from './CodeDestination/CodeDestination.constants'
import {
  CreateCodeArguments,
  useBuildCreateCodePayload
} from '../CreateFlowcodeForm/CreateFlowcodeFormDesktop/CreateFlowcodeFormDesktop.hooks'
import { FC, createContext, useContext, useState } from 'react'
import { LandingPageFormValuesType } from '@dtx-company/inter-app/src/types/flowcode'
import { Layout } from '@app/pre-build/src/components/OnboardingFlows/Components/Layout/Layout'
import { PrimaryButton } from '@dtx-company/shared-components/src/components/atoms/Button/index'
import { SocialFormFields } from '@app/pre-build/src/components/OnboardingFlows/Flowtag/OnboardingForms/SocialForm/SocialForm.types'
import { SocialFormInputType } from '@app/pre-build/src/components/OnboardingFlows/Flowtag/OnboardingForms/SocialForm/SocialForm.constants'
import { useCombinedFunnelMobileV1State } from './CombinedFunnelMobile.hooks'

type Destination = [SocialFormFields, SocialFormInputType]

interface MobileDestinationContextValue {
  destination: Destination
  setDestination: (destination: Destination) => void
  value: string
  setValue: (value: string) => void
}

const defaultMobileDestinationContextValue: MobileDestinationContextValue = {
  destination: COMBINED_FUNNEL_FORM_INPUT_ENTRIES[0],
  setDestination: () => undefined,
  value: '',
  setValue: () => undefined
}

const MobileDestinationContext = createContext(defaultMobileDestinationContextValue)

export function useMobileDestination(): MobileDestinationContextValue {
  return useContext(MobileDestinationContext)
}

//currently, we only support these three types of fields on mobile (link, sms, instagram)
function injectDestinationFormValue(
  destination: Destination,
  value: string,
  formValues: LandingPageFormValuesType
): LandingPageFormValuesType {
  switch (destination[0]) {
    case SocialFormFields.LINK:
      formValues.link = value
      break

    case SocialFormFields.SMS:
      formValues.sms = { message: '', phoneNumber: value }
      break

    case SocialFormFields.INSTAGRAM:
      formValues.link = `https://www.instagram.com/${value}`
      break

    default:
      break
  }

  return formValues
}

export function useCreateMobileCodeArgs(): CreateCodeArguments {
  const createCodeArgs = useBuildCreateCodePayload()
  const { destination, value } = useMobileDestination()

  const destinationFormValues = injectDestinationFormValue(
    destination,
    value,
    createCodeArgs.destinationFormValues
  )

  return {
    ...createCodeArgs,
    destinationFormValues
  }
}

export const CombinedFunnelMobile: FC = () => {
  const {
    Component,
    componentId,
    showBackButton,
    showNextButton,
    allowNextStep,
    currStep,
    totalSteps,
    showProgressBar,
    setAllowNextStep,
    advanceStep,
    handleBack
  } = useCombinedFunnelMobileV1State()

  const [destination, setDestination] = useState<[SocialFormFields, SocialFormInputType]>(
    COMBINED_FUNNEL_FORM_INPUT_ENTRIES[0]
  )

  const [value, setValue] = useState('')

  return (
    <MobileDestinationContext.Provider value={{ destination, setDestination, value, setValue }}>
      <Box
        flexDirection={'column'}
        height="calc(100vh - 128px)"
        width="100vw"
        data-testid={`cf_${componentId}`}
      >
        <Box flexGrow={1}>
          <Layout
            currStep={currStep}
            totalSteps={totalSteps}
            showBackButton={showBackButton}
            showProgressBar={showProgressBar}
            handleBack={handleBack}
            navigationBoxProps={{
              m: showBackButton ? '10px 24px 6px' : '10px 24px 16px 0'
            }}
          >
            <Component setAllowNextStep={setAllowNextStep} advanceStep={advanceStep} />
          </Layout>
        </Box>
        {showNextButton && (
          <Box
            position={'fixed'}
            bottom={0}
            width={'100%'}
            padding={'16px'}
            background="primary.white"
          >
            <PrimaryButton
              onClick={advanceStep}
              width="100%"
              disabled={!allowNextStep}
              data-testid="next-btn"
              data-heap-id="CombinedFunnelMobile_NextButton"
            >
              Next
            </PrimaryButton>
          </Box>
        )}
      </Box>
    </MobileDestinationContext.Provider>
  )
}
